import {
  Box,
  Heading,
  SimpleGrid,
  Image,
  Text,
  VStack,
  useColorModeValue,
  Divider,
  Badge,
  Icon,
  Flex,
  HStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FaUserMd, FaHeart, FaQuoteLeft, FaHospital } from "react-icons/fa";

const MotionBox = motion(Box);

function Collaborators() {
  const teamMembers = [

    {
      name: "Dr. Mohamed Zghal",
      position: "Clinician",
      image: "/imgs/team/mohamedzghal.jpg",
    },
    {
      name: "Dr. Olfa Messaoud",
      position: "Geneticist",
      image: "/imgs/team/olfamessaoud.png",
    },
    {
      name: "Amir Bouaziz",
      position: "Bioinformatician",
      image: "/imgs/team/amirbouaziz.jpg",
    },
    {
      name: "Hazem Jday",
      position: "Software Engineer",
      image: "/imgs/team/hazemjday.jpg",
    },
  ];

  const sponsors = [
    {
      name: "AGYA - Arab German Young Academy",
      logo: "/imgs/team/agya.png",
      description:
        "AGYA supports young scientists by creating an interdisciplinary network of Arab and German scholars to foster dialogue and research.",
    },
    {
      name: "L'association d'aide aux enfants atteints de Xeroderma Pigmentosum",
      isAssociation: true,
      logo: "/imgs/team/enfantsdelalune.jpg",
      description:
        "Association d'aide aux enfants atteints de xeroderma pigmentosum en Tunisie. Active NGO supporting patient protection, medical equipment, and family care.",
    },
    {
      name: "Institut Pasteur de Tunis",
      logo: "/imgs/team/ipt.png",
      description:
        "Institut Pasteur de Tunis focuses on biomedical research and public health, contributing to scientific advancements in Tunisia and beyond.",
    },

  ];

  const hospitalCollaborators = [
    {
      service: "Service de Dermatologie",
      hospital: "Hôpital La Rabta",
      city: "Tunis, Tunisie",
    },
    {
      service: "Service de Dermatologie",
      hospital: "Hôpital Charles Nicolle",
      city: "Tunis, Tunisie",
    },
    {
      service: "Service de Dermatologie",
      hospital: "Hôpital Habib Thameur",
      city: "Tunis, Tunisie",
    },
  ];

  const cardBg = useColorModeValue(
    "rgba(255, 255, 255, 0.75)",
    "rgba(15, 23, 42, 0.75)"
  );
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const headingColor = useColorModeValue("slate.800", "white");
  const textColor = useColorModeValue("slate.600", "slate.300");

  return (
    <Box id="team" as="section" py={12} position="relative">
      <Box mb={10} textAlign="center">
        <Heading
          size="xl"
          textAlign="center"
          color={headingColor}
          fontWeight="900"
          letterSpacing="-0.02em"
        >
          Meet Our{" "}
          <Text as="span" bgGradient="linear(to-r, teal.500, cyan.400)" bgClip="text">
            Team & Sponsors
          </Text>
        </Heading>
      </Box>

      {/* Team Subheading */}
      <Heading
        size="md"
        textAlign="center"
        color="teal.500"
        mb={8}
        fontWeight="700"
        letterSpacing="0.05em"
        textTransform="uppercase"
      >
        Our Team
      </Heading>

      <SimpleGrid
        columns={{ base: 1, sm: 2, lg: 4 }}
        spacing={6}
        maxW="1200px"
        mx="auto"
        px={6}
      >
        {teamMembers.map((member, index) => (
          <MotionBox
            key={index}
            role="group"
            bg={cardBg}
            backdropFilter="blur(20px)"
            p={6}
            borderRadius="24px"
            border="1px solid"
            borderColor={cardBorder}
            shadow={useColorModeValue(
              "0 10px 30px rgba(0,0,0,0.05)",
              "0 10px 30px rgba(0,0,0,0.3)"
            )}
            textAlign="center"
            whileHover={{ y: -8, shadow: "0 20px 40px rgba(13, 148, 136, 0.2)" }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
          >
            <VStack spacing={3}>
              <Box position="relative">
                <Box
                  position="absolute"
                  inset="-4px"
                  borderRadius="full"
                  bgGradient="linear(to-br, teal.400, cyan.400)"
                  shadow="0 0 15px rgba(13, 148, 136, 0.4)"
                />
                <Image
                  src={member.image}
                  alt={member.name}
                  boxSize="110px"
                  objectFit="cover"
                  borderRadius="full"
                  position="relative"
                  border="3px solid white"
                  filter="grayscale(100%)"
                  transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                  _groupHover={{
                    filter: "grayscale(0%)",
                    transform: "scale(1.04)",
                  }}
                  _hover={{
                    filter: "grayscale(0%)",
                    transform: "scale(1.04)",
                  }}
                />
              </Box>
              <Text fontWeight="800" fontSize="lg" color={headingColor} mt={2}>
                {member.name}
              </Text>
              <Badge
                colorScheme="teal"
                borderRadius="full"
                px="12px"
                py="3px"
                fontSize="xs"
                fontWeight="700"
              >
                {member.position}
              </Badge>
            </VStack>
          </MotionBox>
        ))}
      </SimpleGrid>

      {/* Sponsors Subheading */}
      <Heading
        size="md"
        textAlign="center"
        color="teal.500"
        mt={16}
        mb={8}
        fontWeight="700"
        letterSpacing="0.05em"
        textTransform="uppercase"
      >
        Our Sponsors
      </Heading>

      <SimpleGrid
        columns={{ base: 1, md: 3 }}
        spacing={8}
        maxW="1200px"
        mx="auto"
        px={6}
      >
        {sponsors.map((sponsor, index) => (
          <MotionBox
            key={index}
            bg={cardBg}
            backdropFilter="blur(20px)"
            p={8}
            borderRadius="24px"
            border="1px solid"
            borderColor={cardBorder}
            shadow={useColorModeValue(
              "0 10px 30px rgba(0,0,0,0.05)",
              "0 10px 30px rgba(0,0,0,0.3)"
            )}
            textAlign="center"
            whileHover={{ y: -8, shadow: "0 20px 40px rgba(13, 148, 136, 0.2)" }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.15 }}
          >
            <VStack spacing={4}>
              <Box
                w="100%"
                h="160px"
                p={4}
                bg={useColorModeValue("white", "slate.800")}
                borderRadius="20px"
                shadow="xs"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {sponsor.logo ? (
                  <Image
                    src={sponsor.logo}
                    alt={sponsor.name}
                    maxH="130px"
                    maxW="240px"
                    objectFit="contain"
                  />
                ) : (
                  <VStack spacing={2}>
                    <Icon as={FaHeart} boxSize="42px" color="teal.400" />
                    <Text fontWeight="800" fontSize="sm" color="teal.600">
                      Enfants de la Lune Tunisie
                    </Text>
                  </VStack>
                )}
              </Box>
              <Text fontWeight="800" fontSize="md" color={headingColor}>
                {sponsor.name}
              </Text>
              <Text color={textColor} fontSize="sm" lineHeight="1.6">
                {sponsor.description}
              </Text>
            </VStack>
          </MotionBox>
        ))}
      </SimpleGrid>

      {/* Special Thanks Callout */}
      <SimpleGrid
        mt={16}
        columns={{ base: 1, md: 1 }}
        maxW="1200px"
        mx="auto"
        px={6}
      >
        <MotionBox
          position="relative"
          overflow="hidden"
          bg={useColorModeValue(
            "linear-gradient(135deg, rgba(13, 148, 136, 0.08) 0%, rgba(6, 182, 212, 0.08) 100%)",
            "linear-gradient(135deg, rgba(13, 148, 136, 0.18) 0%, rgba(6, 182, 212, 0.12) 100%)"
          )}
          backdropFilter="blur(24px)"
          p={{ base: 8, md: 12 }}
          borderRadius="32px"
          border="1.5px solid"
          borderColor={useColorModeValue("teal.300", "teal.600")}
          shadow={useColorModeValue(
            "0 20px 40px rgba(13, 148, 136, 0.15)",
            "0 20px 50px rgba(0, 0, 0, 0.5)"
          )}
          textAlign="center"
          whileHover={{ y: -4, shadow: "0 25px 50px rgba(13, 148, 136, 0.25)" }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Background Ambient Glow & Quote Watermark */}
          <Box
            position="absolute"
            top="-30%"
            left="50%"
            transform="translateX(-50%)"
            w="350px"
            h="350px"
            borderRadius="full"
            bg="radial-gradient(circle, rgba(13, 148, 136, 0.2) 0%, rgba(0,0,0,0) 70%)"
            pointerEvents="none"
          />
          <Icon
            as={FaQuoteLeft}
            position="absolute"
            top="20px"
            left="24px"
            boxSize="60px"
            color={useColorModeValue("teal.400", "teal.600")}
            opacity="0.1"
            pointerEvents="none"
          />

          <VStack spacing={6} position="relative" zIndex="1">
            {/* Top Medical Doctor Icon Badge */}
            <Flex
              w="64px"
              h="64px"
              borderRadius="full"
              bgGradient="linear(to-br, teal.400, cyan.500)"
              color="white"
              align="center"
              justify="center"
              shadow="0 8px 25px rgba(13, 148, 136, 0.4)"
              mx="auto"
            >
              <Icon as={FaUserMd} boxSize="28px" />
            </Flex>

            {/* Title with Gradient Text */}
            <Heading
              size="lg"
              fontWeight="900"
              letterSpacing="-0.02em"
              bgGradient="linear(to-r, teal.500, cyan.400)"
              bgClip="text"
            >
              Special Thanks to Our Collaborators
            </Heading>

            {/* Hospital Dermatology Collaborator Cards */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5} w="100%" pt={2}>
              {hospitalCollaborators.map((collab, idx) => (
                <Box
                  key={idx}
                  p={5}
                  bg={useColorModeValue("whiteAlpha.900", "slate.800")}
                  borderRadius="20px"
                  border="1px solid"
                  borderColor={cardBorder}
                  shadow="sm"
                  textAlign="center"
                >
                  <Flex
                    w="48px"
                    h="48px"
                    borderRadius="16px"
                    bg="teal.50"
                    color="teal.600"
                    _dark={{ bg: "whiteAlpha.100", color: "teal.300" }}
                    align="center"
                    justify="center"
                    mx="auto"
                    mb={3}
                  >
                    <Icon as={FaHospital} boxSize="22px" />
                  </Flex>
                  <Text fontSize="sm" fontWeight="800" color={headingColor}>
                    {collab.service}
                  </Text>
                  <Text fontSize="md" fontWeight="900" color="teal.500" mt={1}>
                    {collab.hospital}
                  </Text>
                  <Text fontSize="xs" color={textColor} mt={1}>
                    {collab.city}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </MotionBox>
      </SimpleGrid>

      <Divider my={12} maxW="1200px" mx="auto" borderColor={cardBorder} />
    </Box>
  );
}

export default Collaborators;
